import { useMemo, useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import type { Building, Finding, FindingCategory } from "@/lib/api/building";
import { useLensFindingsOverview } from "@/lib/useLensFindingsOverview";
import { TrackSelectorCard } from "@/components/building/findings/TrackSelectorCard";
import type { ActivityCategory } from "@/lib/api/activity";
import { useBuildings } from "@/lib/api/useBuildings";
import { useFilteredBuildings } from "@/lib/inventory-scope";
import { useInventoryFilter } from "@/lib/inventory-filter";
import { cardBase, cardToneText } from "@/lib/card-styles";
import type { EntityKind } from "@/lib/entity-kind";
import type { OverviewCopy } from "@/lib/overview-lens";
import { cn } from "@/lib/utils";
import { ScopedLink } from "@/lib/scoped-nav";
import { KbCycleRibbon } from "./KbCycleRibbon";
import { PerspectiveOverview } from "./PerspectiveOverview";
import {
  cycleSlotLensFromTrack,
  openFindings,
  type LensFindingFilter,
} from "./lens-topic-metrics";

// Building-scope header links to the Toiminnot — same quiet-link shape as
// ActivityFeed.tsx's "Katso koko loki →" (:198-205). Not a re-created tab bar: plain
// inline text, no icon tiles, no KPI value/hint/tone, no role="tablist", no selected state.
const QUIET_TOIMINTO_LINK_CLASS =
  "text-sm font-medium text-primary hover:underline";

export type LensTrackConfig = {
  id: string;
  filter: LensFindingFilter;
  label: string;
  trackCard?: {
    icon: LucideIcon;
    detail: string;
  };
  summaryOptions?: {
    okHint?: string;
    openHint?: string;
    zoneCount?: number | null;
  };
  activityCategories: ActivityCategory[];
  defaultEntityKind?: EntityKind;
  findingsList: {
    category?: FindingCategory;
    sourceType?: Finding["source_type"];
    emptyMessage: string;
  };
  subHeaderSuffix: string;
  /** This lens's fill of the shared kanta/salkku overview grammar (situation-card subtitle, map
   *  legend, …) — the one place this track's portfolio-scope copy lives (renamed from the former
   *  `portfolioRollup: { titlePrefix, subtitle }`, #1296 task #1308). */
  overviewCopy: OverviewCopy;
};

export type LensTopicSectionConfig = {
  eyebrow: string;
  title: string;
  description: string;
  tracks: LensTrackConfig[];
  /** Default building branch: ribbon + this panel (Datan laatu, Talotekniikka, …). */
  renderSummaryPanel?: (
    buildingId: string,
    findings: Finding[] | null,
  ) => ReactNode;
  /**
   * When set, building scope is this workspace alone — it owns header + ribbon
   * (Energiatehokkuus snapshot loop). Kanta/salkku still uses this section's
   * portfolio overview; do not also pass a summary panel for that path.
   */
  renderBuildingWorkspace?: (buildingId: string) => ReactNode;
};

export function LensTopicSection({
  eyebrow,
  title,
  description,
  tracks,
  renderSummaryPanel,
  renderBuildingWorkspace,
}: LensTopicSectionConfig) {
  const { buildingId } = useInventoryFilter();
  const inventory = useBuildings();
  const scoped = useFilteredBuildings(
    inventory.status === "ok" ? inventory.buildings : [],
  );

  const [selectedTrackId, setSelectedTrackId] = useState(tracks[0]?.id ?? "");

  const selectedTrack =
    tracks.find((t) => t.id === selectedTrackId) ?? tracks[0];

  const scopeBuildings: Building[] = useMemo(() => {
    if (buildingId) {
      const one = scoped.find((b) => b.id === buildingId);
      return one ? [one] : [];
    }
    return scoped;
  }, [buildingId, scoped]);

  // Same load path as left-nav Havainnot (useScopedFindings, story #1248), wrapped by
  // useLensFindingsOverview so the one scoped Finding[] this needs also derives the kanta/salkku
  // overview vocabulary for the portfolio branch below — no second GET /findings.
  const { findings, overview } = useLensFindingsOverview(
    {
      category: selectedTrack.findingsList.category,
      sourceType: selectedTrack.findingsList.sourceType,
    },
    scopeBuildings,
  );

  if (inventory.status === "error") {
    return (
      <div className={cn(cardBase, "p-5 text-sm text-destructive")}>
        Dataa ei voitu ladata: {inventory.message}
      </div>
    );
  }

  if (inventory.status === "loading") {
    return (
      <div className={cn(cardBase, "p-5 text-sm text-muted-foreground")}>
        Ladataan…
      </div>
    );
  }

  // Energiatehokkuus (and any similar loop) owns its building chrome; skip the shared header.
  if (buildingId && renderBuildingWorkspace) {
    return <>{renderBuildingWorkspace(buildingId)}</>;
  }

  return (
    <div className="space-y-5">
      <LensHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
      />

      {buildingId ? (
        // Building scope is the workspace itself — no topic tab bar, no in-perspective
        // Havainnot/Tapahtumaloki tabs. Contextual Toiminto doors are the cycle-ribbon
        // slot; the workspace panel is the page.
        <>
          <KbCycleRibbon
            buildingId={buildingId}
            lens={cycleSlotLensFromTrack(findings, selectedTrack.findingsList)}
          />
          {renderSummaryPanel?.(buildingId, findings)}
        </>
      ) : (
        <>
          {tracks.length > 1 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {tracks.map((track) => (
                <TrackSelectorCard
                  key={track.id}
                  icon={track.trackCard!.icon}
                  label={track.label}
                  detail={track.trackCard!.detail}
                  active={selectedTrackId === track.id}
                  onClick={() => setSelectedTrackId(track.id)}
                />
              ))}
            </div>
          ) : null}
          <PerspectiveOverview
            state={overview}
            copy={selectedTrack.overviewCopy}
          />
        </>
      )}
    </div>
  );
}

// The eyebrow/title/description header every LensTopicSection consumer shows, plus optional
// `right` chrome. The four judgment / optimization pages pass none — their Toiminto doors are
// the cycle-ribbon slot. Exported so a `renderBuildingWorkspace` (Energiatehokkuus loop) can
// match the same eyebrow chrome while owning its own ribbon + snapshot load.
export function LensHeader({
  eyebrow,
  title,
  description,
  right,
}: {
  eyebrow: string;
  title: string;
  description: string;
  right?: ReactNode;
}) {
  return (
    <header className="flex items-start justify-between gap-4 px-1">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {eyebrow}
        </div>
        <h2 className="font-display text-lg tracking-tight">{title}</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      {right}
    </header>
  );
}

// The quiet Toiminto caption Konfiguraatio still shows at building scope: open-Havainnot
// count (linking to the filtered /findings list) and the shared Tapahtumaloki link.
// The four judgment / optimization pages do not render this — their doors are the
// cycle-ribbon slot (docs/spec/concepts.md § Six perspectives). Extracted so Konfiguraatio —
// which is not a `LensTopicSection` consumer — can keep the chrome without a hand-rolled
// second header. Ajettu · seuraava and the 24 h cycle ribbon live on
// KbCycleRibbon's own panel, not here.
export function LensQuietCaption({
  findings,
  filter,
}: {
  findings: Finding[] | null;
  filter: LensFindingFilter;
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-end gap-x-3 gap-y-1 shrink-0">
      <HavainnotHeaderLink findings={findings} filter={filter} />
      {/* Scope-only — story #1289 owns /activity's filter axes, so no perspective param. */}
      <ScopedLink to="/activity" className={QUIET_TOIMINTO_LINK_CLASS}>
        Tapahtumaloki →
      </ScopedLink>
    </div>
  );
}

function HavainnotHeaderLink({
  findings,
  filter,
}: {
  findings: Finding[] | null;
  filter: LensFindingFilter;
}) {
  const open = findings === null ? null : openFindings(findings, filter);
  const count = open === null ? "…" : String(open.length);
  const critical =
    open === null
      ? 0
      : open.filter((finding) => finding.severity === "critical").length;
  return (
    <ScopedLink
      to="/findings"
      search={(prev) => ({
        ...prev,
        category: filter.category,
        source: filter.sourceType,
      })}
      className={QUIET_TOIMINTO_LINK_CLASS}
    >
      Havainnot <span className="tabular-nums">{count}</span>
      {critical > 0 ? (
        <>
          {" · "}
          <span className={cn("tabular-nums", cardToneText.warn)}>
            {critical} kriittistä
          </span>
        </>
      ) : null}{" "}
      →
    </ScopedLink>
  );
}
